import { buildUrl } from 'config/routes'
import { useRootStore } from 'hooks/rootStore'
import type { QueryParams } from 'mobx-router'
import {
  useCallback,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from 'react'

type InternalLinkProps = {
  /** Route name from routes config */
  to: string
  /** Path parameters (e.g., { slideDeckId: '123' }) */
  params?: QueryParams
  /** Query string parameters */
  queryParams?: QueryParams
  children: ReactNode
  allowPropagation?: boolean
} & Omit<ComponentPropsWithoutRef<'a'>, 'href'>

export function InternalLink({
  to,
  params = {},
  queryParams = {},
  children,
  onClick,
  ...rest
}: InternalLinkProps) {
  const rootStore = useRootStore()

  const href = buildUrl(to, params, queryParams)

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (!rest.allowPropagation) {
        e.stopPropagation()
      }
      if (onClick) {
        onClick(e)
        if (e.defaultPrevented) return
      }
      // Allow cmd+click (Mac) / ctrl+click (Windows) to open in new tab
      if (e.metaKey || e.ctrlKey) {
        // pass - allow the default browser behavior to open the link in a new tab
      } else {
        e.preventDefault()
        rootStore.navigateTo(to, params, queryParams)
      }
    },
    [onClick, rest.allowPropagation, rootStore, to, params, queryParams]
  )

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  )
}
